import { FormattedMessage } from 'react-intl';

import { cn } from '@packages/shared/src/utils/cn/cn';
import {
  ColorButton,
  type ColorButtonProps,
} from '@packages/shared/src/ui/ColorButton/ColorButton';

import styles from './ProductCardV2.ColorSwitcher.module.scss';

/**
 * Product Card Color Switcher
 *
 * Renders up to 5 color buttons. CSS container queries hide extras based on available width.
 * CSS counters calculate and display the hidden count automatically.
 */
export const MAX_RENDERED_COLORS = 5;

export interface ProductCardV2ColorSwitcherProps {
  /** Array of color options */
  colors: readonly ColorButtonProps[];
  /** Callback when a color is selected */
  onColorChange?: (index: number) => void;
  /** Additional classes */
  className?: string;
  /** Currently selected color */
  activeIndex: number;
  /** Loading state if data fetching is necessary to update colors */
  isLoading?: boolean;
}

export const ProductCardV2ColorSwitcher = ({
  colors,
  onColorChange,
  className,
  activeIndex,
  isLoading,
}: ProductCardV2ColorSwitcherProps) => {
  if (!colors?.length) return null;

  const visibleColors = colors.slice(0, MAX_RENDERED_COLORS);

  return (
    <div
      className={cn(
        'pointer-events-none relative z-20 mx-2 mt-1 flex items-center gap-1 [grid-area:9/2/span_1/span_1]',
        styles.container,
        className,
      )}
      style={
        {
          '--max-visible-colors': visibleColors.length,
          '--total-colors': colors.length,
        } as React.CSSProperties
      }
      role="radiogroup"
    >
      <div className={`flex items-center gap-1 ${styles.list}`}>
        {visibleColors.map((colorProps, index) => (
          <ColorButton
            key={colorProps.name}
            {...colorProps}
            active={activeIndex === index && !isLoading}
            isSelected={activeIndex === index}
            aria-atomic="true"
            role="radio"
            aria-checked={activeIndex === index}
            onClick={() => onColorChange?.(index)}
            className={`pointer-events-auto ${styles.button}`}
          />
        ))}
        {colors.length > 2 && (
          <span className={`text-size-body-xs whitespace-nowrap ${styles.more}`}>
            <FormattedMessage
              id="product-colors.hidden-colors-label"
              defaultMessage="+{count} Farben"
              values={{
                count: <span className={styles.count} />,
              }}
            />
          </span>
        )}
      </div>
    </div>
  );
};
